import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import clsx from 'clsx'
import React from 'react'

import { getTimeAgo } from '~/lib/date'
import { makeShareTweetUrl, makeTwitterProfileUrl } from '~/lib/twitter'
import Text from '~/components/Text/Text'
import Button from '~/components/Button/Button'
import { Author, Post, PostCta } from '~/content/blog/types'
import { BLOG_FULL_URL } from '~/constants'

import styles from './PostArticle.module.sass'

// Optimization made to decrease first load JS.
const Content = dynamic(() => import('~/components/PostContent/PostContent'))

const PostArticle: React.FC<Post> = (post) => {
  const standardCtas: PostCta[] = [
    {
      title: 'Share',
      url: makeSharePostUrl(post),
      icon: 'twitter',
    },
  ]
  const topCtas: PostCta[] = [...standardCtas, ...post.ctas.filter((cta) => !cta.hideFromTop)]
  const bottomCtas: PostCta[] = [...standardCtas, ...post.ctas]
  return (
    <div className={clsx(styles.article, 'site-container site-grid')}>
      <div className='col-span-full lg:col-start-4 lg:col-end-10'>
        <header className={styles.header}>
          <Text suppressHydrationWarning={true} variant='caption' fontSize={[12, 10, 12]}>
            {getTimeAgo(post.date)}
          </Text>
          <Text variant='subheading' as='h1' fontSize={[28, 28, 30]} className={styles.title}>
            {post.title}
          </Text>
          <PostAuthors authors={post.authors} />
          <PostShareButtons ctas={topCtas} postUrl={post.url} />
        </header>
        <Content code={post.code} />
        <PostShareButtons ctas={bottomCtas} postUrl={post.url} />
      </div>
    </div>
  )
}

export default PostArticle

const PostShareButtons = ({ ctas, postUrl }: { ctas: PostCta[]; postUrl: string }) => (
  <>
    {ctas.map((cta) => (
      <React.Fragment key={cta.url}>
        <PostShareButton {...cta} postUrl={postUrl} />{' '}
      </React.Fragment>
    ))}
  </>
)

const PostShareButton = (cta: PostCta & { postUrl: string }) => (
  <Button
    variant='share'
    subVariant='sharePost'
    icon={cta.icon}
    className={styles.shareButton}
    external
    link={cta.url}
    data-track={`cta:share-post:${cta.postUrl}@${cta.icon}`}
  >
    {cta.title}
  </Button>
)

const PostAuthors = ({ authors }: { authors: Author[] }) => (
  <div className={styles.authors}>
    {authors.map((author) => (
      <PostAuthor author={author} key={author.name} />
    ))}
  </div>
)

const PostAuthor = ({ author }: { author: Author }) => (
  <a
    className={styles.author}
    href={makeTwitterProfileUrl(author.twitter)}
    target='_blank'
    rel='noopener noreferrer'
    data-track={`external:go-to-author-twitter:@${author.twitter}`}
  >
    <div className={styles.authorImageWrapper}>
      <Image
        src={author.avatar}
        alt={author.name}
        width={40}
        height={40}
        layout='fixed'
        className={styles.authorImage}
      />
    </div>

    <div className={styles.authorText}>
      <Text variant='body' fontSize={[15, 15, 16]} className={styles.authorName}>
        {author.name}
      </Text>
      <Text variant='caption' fontSize={[11, 11, 12]} className={styles.authorTwitter}>
        @{author.twitter}
      </Text>
    </div>
  </a>
)

const makeSharePostUrl = (post: Post) => {
  const usernames = post.authors.map((author) => author.twitter)
  const mentions = usernames.map((username) => `@${username}`).join(' ')

  return makeShareTweetUrl({
    text: `“${post.title}” by ${mentions}`,
    url: `${BLOG_FULL_URL}/${post.slug}`,
    related: usernames,
  })
}
